import type { ReactNode, SyntheticEvent } from 'react'
import React from 'react'

import {
  AlignItems,
  AlignSelf,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  Heading6,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import type { SearchStandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getDefaultImage, getSrcSet } from '../../utils'
import { SubtitleTagRow } from '../RecipeGrid/RecipeTile/RecipeTileBody/SubtitleTagRow'
import { DietaryWarningTag } from '../Tags'

const IMAGE_WIDTH = 112
const IMAGE_SIZE = `${IMAGE_WIDTH}px`
const ACTION_SIZE = '40px'
const TILE_HEIGHT = '152px'

export type DesktopSearchRecipeTileProps = {
  recipe: SearchStandardRecipeItem
  isInBasket: boolean
  canAddRecipes: boolean
  reachedLimit: boolean
  onClick: (recipeId: RecipeUUID) => void
  onAddClick: (recipeId: RecipeUUID) => void
  onRemoveClick: (recipeId: RecipeUUID) => void
  onBlockedAddAttempt: () => void
  flexPortionControl?: ReactNode
  isAuthenticated: boolean
}

export const DesktopSearchRecipeTile = ({
  recipe,
  isInBasket,
  canAddRecipes,
  reachedLimit,
  onClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  flexPortionControl,
  isAuthenticated,
}: DesktopSearchRecipeTileProps) => {
  const imageUrl = getDefaultImage(recipe.image.crops)
  const isUnavailable = recipe.isAvailable === false && !isInBasket
  const isAddDisabled = isUnavailable || (reachedLimit && !isInBasket)
  const invalidPreference = recipe.tagGroups.general?.find(
    ({ type }) => type === 'invalidPreference',
  )

  const handleAction = (event: SyntheticEvent) => {
    event.stopPropagation()

    if (isInBasket) {
      onRemoveClick(recipe.id)
    } else if (!canAddRecipes) {
      onBlockedAddAttempt()
    } else if (!isAddDisabled) {
      onAddClick(recipe.id)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      onClick(recipe.id)
    }
  }

  return (
    <Box
      aria-label={`View ${recipe.title} details`}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      color={Color.ColdGrey_900}
      display={Display.Flex}
      height={isAuthenticated ? TILE_HEIGHT : undefined}
      onClick={() => onClick(recipe.id)}
      onKeyDown={handleKeyDown}
      role="button"
      style={{ cursor: 'pointer', overflow: 'hidden' }}
      tabIndex={0}
    >
      <Box
        alignSelf={AlignSelf.Stretch}
        minHeight={IMAGE_SIZE}
        minWidth={IMAGE_SIZE}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
        width={IMAGE_SIZE}
      >
        {imageUrl && (
          <Image
            alt={recipe.image.altText ?? recipe.title}
            height="100%"
            sizes={IMAGE_SIZE}
            src={imageUrl}
            srcSet={getSrcSet(recipe.image.crops)}
            style={{ display: 'block', objectFit: 'cover' }}
            width={IMAGE_SIZE}
          />
        )}
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1} minWidth="0">
        <Box display={Display.Flex} flexGrow={1} minWidth="0" paddingH={3} paddingV={2}>
          <Box
            alignItems={AlignItems.FlexStart}
            display={Display.Flex}
            flexGrow={1}
            gap={2}
            minWidth="0"
          >
            <Box flexGrow={1} minWidth="0">
              <Heading6
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {recipe.title}
              </Heading6>
              <SubtitleTagRow recipe={recipe} />
              {invalidPreference && (
                <Box paddingV={1}>
                  <DietaryWarningTag text={invalidPreference.text} />
                </Box>
              )}
              {isUnavailable && <Body2 color={Color.ColdGrey_600}>Unavailable</Body2>}
            </Box>
            <Box
              data-testid="desktop-search-recipe-action"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              justifyContent={
                isAuthenticated && !isInBasket ? JustifyContent.FlexEnd : JustifyContent.FlexStart
              }
              minWidth={ACTION_SIZE}
              style={{ alignSelf: 'stretch', flexShrink: 0 }}
            >
              {isAuthenticated && isInBasket ? (
                <Button
                  aria-label={`Remove ${recipe.title}`}
                  colorVariant={ButtonColorVariant.Tertiary}
                  onClick={handleAction}
                  onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
                  width={ACTION_SIZE}
                  style={{ alignSelf: 'flex-end', minHeight: ACTION_SIZE, minWidth: ACTION_SIZE }}
                >
                  <Icon name="delete" size={5} variant={IconVariant.Actionable} />
                </Button>
              ) : (
                <Button
                  aria-label={isInBasket ? `Remove ${recipe.title}` : `Add ${recipe.title}`}
                  colorVariant={
                    isInBasket ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary
                  }
                  disabled={isAddDisabled}
                  icon={isInBasket ? 'remove' : 'add'}
                  onClick={handleAction}
                  onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
                  variant={ButtonVariant.Icon}
                  width={ACTION_SIZE}
                  style={{ alignSelf: 'flex-end', minHeight: ACTION_SIZE, minWidth: ACTION_SIZE }}
                />
              )}
            </Box>
          </Box>
        </Box>
        {isAuthenticated && isInBasket && flexPortionControl && (
          <Box
            alignItems={AlignItems.Center}
            data-testid="desktop-search-flex-footer"
            display={Display.Flex}
            justifyContent={JustifyContent.FlexEnd}
            onClick={(event: SyntheticEvent) => event.stopPropagation()}
            onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
            paddingH={3}
            paddingV={1}
            style={{ boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)' }}
          >
            {flexPortionControl}
          </Box>
        )}
      </Box>
    </Box>
  )
}
